import React, { useEffect, useRef, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'

import styles from './styles.module.scss'

import Spinner from '../../UI/Spinner/Spinner'
import VacancyForm from './VacancyForm/VacancyForm'
import VacancyList from './VacancyList/VacancyList'

import { ReactComponent as FilterIcon } from '../../assets/icons/filter.svg'
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg'
import { ReactComponent as CloseIconWhite } from '../../assets/icons/close-white.svg'

import { getVacancies } from '../../api/getApi'
import { getAuthProfileFilters } from '../../api/getApi'
import { vacanciesSearchHelper } from '../../helpers/vacanciesSearchHelper'
import { salaryCurrenciesHelper } from '../../helpers/vacanciesSearchHelper'
import { objToQueryConverter } from '../../helpers/apiHelper'
import { addVacancy, clearVacancies, setFilters, setVacancies } from '../../redux/actions/vacancies.action'
import useInfiniteScroll from '../../hooks/useInfiniteScroll'
import useViewVacancy from '../../hooks/useViewVacancy'
import NotFoundResult from '../UI/NotFoundResult'
import Button from '../../UI/Button/Button'
import { convertDateToSubmit } from '../../helpers/timeHelper'

const ProfileVacancies = () => {
  const { vacancyId } = useViewVacancy(addVacancy)

  const [excludeId, setExcludeId] = useState(vacancyId)
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)

  const [filtersOptions, setFiltersOptions] = useState({
    ranks: [],
    vesselTypes: [],
    currencies: [],
  })

  const { vacancies, filters } = useSelector(({ vacancies }) => vacancies)

  const dispatch = useDispatch()

  const scrollRef = useRef(null)

  const query = (() => {
    const { show_sea, show_land, join_date, ..._query } = filters

    const exclude_id = (() => {
      if (!excludeId) return ''
      if (isNaN(Number(excludeId))) return ''

      return excludeId
    })()

    const exclude_date = (() => {
      if (!join_date) return ''
      return convertDateToSubmit(join_date)
    })()

    const params = objToQueryConverter({ ..._query, exclude_id, exclude_date })

    return `${params}&show_sea=${show_sea}&show_land=${show_land}`
  })()

  const { handleScroll, dataCount, loading, nextPage } = useInfiniteScroll(
    getVacancies,
    setVacancies,
    vacanciesSearchHelper,
    query,
    clearVacancies
  )

  useEffect(() => {
    return () => {
      dispatch(clearVacancies())
    }

    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    getAuthProfileFilters('rank', 'vessel_type', 'currency', 'salary_rate').then((data) => {
      setFiltersOptions({
        ranks: data.rank,
        vesselTypes: data.vessel_type,
        currencies: salaryCurrenciesHelper(data.currency),
      })
    })
  }, [])

  useEffect(() => {
    if (scrollRef.current) executeScroll()
  }, [filters])

  useEffect(() => {
    if (!isFiltersOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isFiltersOpen])

  const executeScroll = () => scrollRef.current.scrollIntoView()

  const getWorkLocationLabel = () => {
    if (filters.show_sea && filters.show_land) return null
    if (filters.show_sea && !filters.show_land) return 'Onboard Job'
    if (!filters.show_sea && filters.show_land) return 'Shore-Based Job'
    return null
  }

  const chips = (() => {
    const items = []

    const rankLabel = filtersOptions.ranks.find((r) => r.id === filters.rank)?.name
    if (rankLabel) {
      items.push({
        key: 'rank',
        label: rankLabel,
        onDelete: () => dispatch(setFilters({ ...filters, rank: null })),
      })
    }

    const vesselTypeLabel = filtersOptions.vesselTypes.find((v) => v.id === filters.vessel_type)?.name
    if (vesselTypeLabel) {
      items.push({
        key: 'vessel_type',
        label: vesselTypeLabel,
        onDelete: () => dispatch(setFilters({ ...filters, vessel_type: null })),
      })
    }

    const workLocationLabel = getWorkLocationLabel()
    if (workLocationLabel) {
      items.push({
        key: 'work_location',
        label: workLocationLabel,
        onDelete: () => dispatch(setFilters({ ...filters, show_sea: true, show_land: true })),
      })
    }

    if (filters.join_date) {
      items.push({
        key: 'join_date',
        label: filters.join_date,
        onDelete: () => dispatch(setFilters({ ...filters, join_date: null })),
      })
    }

    if (filters.salary) {
      const currency = filtersOptions.currencies.find((c) => c.id === filters.salary_currency)?.name
      const rate = filters.salary_rate ? String(filters.salary_rate) : null
      const salaryTextParts = [String(filters.salary), currency, rate].filter(Boolean)
      items.push({
        key: 'salary',
        label: `Salary: ${salaryTextParts.join(' ')}`,
        onDelete: () =>
          dispatch(
            setFilters({
              ...filters,
              salary: null,
              salary_rate: null,
              salary_currency: null,
            })
          ),
      })
    }

    if (filters.duration) {
      const durationTextParts = [String(filters.duration), filters.duration_period].filter(Boolean).map(String)
      items.push({
        key: 'duration',
        label: `Duration: ${durationTextParts.join(' ')}`,
        onDelete: () => dispatch(setFilters({ ...filters, duration: null, duration_period: null })),
      })
    }

    return items
  })()

  return (
    <div className={styles.vacanciesSearch}>
      <div className={styles.filtersContainer}>
        <div className={styles.filtersControls}>
          <Button
            theme='bordered'
            type='button'
            className={styles.filtersButton}
            onClick={() => setIsFiltersOpen(true)}
          >
            <FilterIcon />
            <span className={styles.filtersButtonText}>Filters</span>
          </Button>
        </div>

        {/*{chips.length !== 0 && (*/}
        {/*  <div className={styles.filtersChipsRow}>*/}
        {/*    {chips.map((chip) => (*/}
        {/*      <div key={chip.key} className={styles.filtersChip}>*/}
        {/*        <div className={styles.filtersChipText}>{chip.label}</div>*/}
        {/*        <button type='button' className={styles.filtersChipRemove} onClick={chip.onDelete}>*/}
        {/*          <CloseIconWhite />*/}
        {/*        </button>*/}
        {/*      </div>*/}
        {/*    ))}*/}
        {/*  </div>*/}
        {/*)}*/}

        <div className={cn(styles.filtersOverlay, { [styles.filtersOverlayOpen]: isFiltersOpen })}>
          <button type='button' className={styles.filtersBackdrop} onClick={() => setIsFiltersOpen(false)} />
          <div className={styles.filtersPanel}>
            <button type='button' className={styles.filtersClose} onClick={() => setIsFiltersOpen(false)}>
              <CloseIcon />
            </button>

            <div className={styles.filtersTitle}>Filters</div>

            <VacancyForm setExcludeId={setExcludeId} setIsFiltersOpen={setIsFiltersOpen} />
          </div>
        </div>
      </div>

      {!vacancies.length && loading ? (
        <Spinner className={styles.vacanciesSpinner} />
      ) : !vacancies.length && !loading ? (
        <NotFoundResult />
      ) : (
        <div className={styles.vacanciesList}>
          <VacancyList
            ref={scrollRef}
            vacancies={vacancies}
            count={dataCount}
            onScroll={handleScroll}
            footer={
              <div className={styles.vacanciesListFooter}>
                {loading && vacancies.length ? (
                  <Spinner className={styles.vacanciesListFooterSpinner} />
                ) : nextPage === null ? (
                  <div className='no-content'>You've seen everything</div>
                ) : null}
              </div>
            }
          />
        </div>
      )}
    </div>
  )
}

export default ProfileVacancies
